import cx from "classnames";
import { useEffect, useImperativeHandle, useState } from "react";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import {
  Button,
  Flex,
  isNullish,
  List,
  Table,
  Text,
  TooltipWrapper
} from "@theorgchartdev/storybook";
import {
  createManualPIT,
  dateStringToDateObject,
  exportPointInTimeArchive,
  getArchiveLabel,
  getPointInTime,
  getResetDateFilters,
  loadPointInTime
} from "./PointInTimeExplorer.utils";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { Globals } from "@coreapp/CoreApp/Globals";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { PointInTimeControls } from "./PointInTimeControls";
import { PointInTimeModal } from "@/appComponents/Blocks/Modals/PointInTimeModal/PointInTimeModal";
import { PointInTimeStatus } from "./PointInTimeStatus";
import { useBaseCharts } from "./useBaseCharts";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { appstr } from "@coreapp/utils/appstr";
import { enumUIModes } from "@coreapp/constants";
import { TABLE_SELECTED_ROW_STYLE } from "@/Utils/Constants";
import { yieldToPaint } from "@coreapp/utils/yieldToPaint";
import PiTBuilderModal from "./PointInTimeBuilderModal";

const BuildBaseChartOptions = (baseCharts, handlers) => {
  return baseCharts.map(chart => {
    const pit = getPointInTime(chart);
    const hasAutoPITEnabled = pit?.enabled ?? false;

    return {
      content: (
        <ActionListItem
          dataTestId={"pit-nav-base-chart-item"}
          flexProps={{
            alignItems: "center"
          }}
          innerFlexProps={{
            alignItems: "center"
          }}
          contentBefore={
            <i
              className={cx("fas", "fa-circle", "oc-pit-icon", {
                "oc-pit-icon__active": hasAutoPITEnabled
              })}
            />
          }
          contentAfter={
            hasAutoPITEnabled ? (
              <Text size="xsmall" colorModifier="success">
                {appstr(pit.frequency.period)}
              </Text>
            ) : null
          }
          label={chart["name"]}
          actions={BuildActions(chart, handlers)}
          visible="always"
          focusable={false}
        />
      ),
      clickHandler: handlers.clickBaseChart,
      value: chart["ID"]
    };
  });
};

const BuildActions = (baseChart, handlers) => [
  {
    iconName: "gear",
    onClick: () => handlers.startConfigure(baseChart),
    dataTestId: "chart-mode-explorer-pit-configure-btn"
  }
];

const BuildPITTableColumns = () => [
  {
    field: "name",
    headerName: appstr("Name"),
    columnWidth: "seventy-five",
    columnOptions: {}
  },
  {
    field: "date",
    headerName: appstr("Date"),
    columnWidth: "twenty-five",
    columnOptions: {
      color: "#8b8b91"
    }
  }
];

const BuildTableData = state => {
  const pit = getPointInTime(state.chart);
  if (isNullish(pit)) {
    return [];
  }

  const filteredArchives = pit.pitlist.filter(archive => {
    const archiveDate = dateStringToDateObject(archive.split(":")[0]);
    if (state.dateFrom && archiveDate < state.dateFrom) {
      return false;
    }
    if (state.dateTo && archiveDate > state.dateTo) {
      return false;
    }
    return true;
  });

  return filteredArchives.map(archive => ({
    value: archive,
    name: `${state.chart["name"]} Archive`,
    date: getArchiveLabel(archive)
  }));
};

export const PointInTimeExplorer = props => {
  const nav = useAppNavigate();
  const { baseCharts, updateBaseChart } = useBaseCharts();
  const [configureBaseChart, setConfigureBaseChart] = useState(null);
  const [isRFCActive, setRFCActive] = useState(false);
  const [isCreatingBuilder, setIsCreatingBuilder] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedBaseChartID, setSelectedBaseChartID] = useState(null);
  const [builderInfo, setBuilderInfo] = useState(null);
  const selectedBaseChart = baseCharts.find(c => c["ID"] === selectedBaseChartID);

  const updateDateFilters = chart => {
    const [nextDateFrom, nextDateTo] = getResetDateFilters(chart);
    setDateFrom(nextDateFrom);
    setDateTo(nextDateTo);
  };

  const {
    chartExplorer: { closeChartExplorer }
  } = useNavigationContext();

  useEffect(() => {
    if (!isNullish(selectedBaseChart)) {
      updateDateFilters(selectedBaseChart);
      return;
    }

    const [firstChart] = baseCharts;

    if (isNullish(firstChart)) {
      return;
    }

    setSelectedBaseChartID(firstChart["ID"]);
    updateDateFilters(firstChart);
  }, [baseCharts]);

  useImperativeHandle(props.handleRef, () => ({
    OpenPointInTimeBuilder
  }));

  const gridOptions = {
    getRowStyle: (data, _, isHovered) => {
      if (props.selectedPointInTime?.value === data.value) {
        return TABLE_SELECTED_ROW_STYLE;
      }

      if (isHovered) {
        return { background: "#ececed", cursor: "pointer" };
      }
    },
    onRowClick: props.onPointInTimeClick
  };

  const OpenPointInTimeBuilder = async ({ value }) => {
    setIsCreatingBuilder(false);
    const pit = await loadPointInTime(selectedBaseChartID, value);
    const { builderInfo } = JSON.parse(pit.data);
    setBuilderInfo(builderInfo);
  };

  const closeBuilder = () => {
    setIsCreatingBuilder(false);
    setBuilderInfo(null);
  };

  const handleCreate = async (builderSettingsInfo = {}) => {
    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    if (!doc || !wsMgr) return;
    if (isNullish(builderInfo)) return;

    const currentBuilderInfo = { ...builderInfo };
    setIsCreatingBuilder(true);
    await yieldToPaint();

    wsMgr.DoProgress(appstr("buildingchart"));
    await yieldToPaint();

    const newChartID = currentBuilderInfo.ID;
    if (builderSettingsInfo.topBoxID != null) {
      currentBuilderInfo.topBoxID = builderSettingsInfo.topBoxID;
    }

    const newDoc = wsMgr.InitializeDoc({}, false, true);
    wsMgr.fBuilderInfo = builderSettingsInfo;
    wsMgr.GetChartMetadata(newDoc, newChartID, { type: "Archive" });

    // Close explorer first — its setSearchParams closure is pre-nav, so
    // calling it after nav.toChart wipes the create+mode params we just set.
    closeChartExplorer();

    const oi = wsMgr.GetOrgInfo();
    const prevSetting = oi.GetOpenDoc();
    oi.SetOpenDoc(false);
    nav.toChart(String(newChartID), { create: true, mode: "edit" });
    oi.SetOpenDoc(prevSetting);

    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("point_in_time.chart.created");
  };

  const handleClickBaseChart = option => {
    const chartObj = baseCharts.find(c => c["ID"] === option.value);
    const [nextDateFrom, nextDateTo] = getResetDateFilters(chartObj);
    setDateFrom(nextDateFrom);
    setDateTo(nextDateTo);
    props.setSelectedPointInTime(null);
    setSelectedBaseChartID(option.value);
  };

  const handleManualArchive = () => {
    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      `${appstr("Create")} ${appstr("Archived Charts").toLowerCase()}?`,
      appstr("Do you want to create a manual Archive for") + ": " + selectedBaseChart["name"] + "?",
      async () => {
        try {
          setRFCActive(true);
          const res = await createManualPIT(selectedBaseChart);
          const updatedChart = JSON.parse(res["data"]);
          updateBaseChart(updatedChart);
        } finally {
          setRFCActive(false);
        }
      },
      () => {},
      `${appstr("Create")} ${appstr("Archived Charts").toLowerCase()}`,
      appstr("Cancel")
    );
    return;
  };

  const handleExportArchive = async () => {
    if (isNullish(props.selectedPointInTime)) return;
    try {
      setRFCActive(true);
      await exportPointInTimeArchive(selectedBaseChartID, props.selectedPointInTime.value);
    } finally {
      setRFCActive(false);
    }
  };

  const wsMgr = GetWSMgr();
  const { isFullAccess } = usePermissionCheck();
  const hasExcelExport = Boolean(
    Globals._uiConfig?.fCanExportExcel && wsMgr?.GetUserInfo?.()?.CanExportExcel?.()
  );

  const handlers = {
    clickBaseChart: handleClickBaseChart,
    startConfigure: setConfigureBaseChart
  };

  const handleCloseConfigure = () => {
    setConfigureBaseChart(null);
  };

  const baseChartOptions = BuildBaseChartOptions(baseCharts, handlers);
  const tableData = BuildTableData({ chart: selectedBaseChart, dateFrom, dateTo });

  const baseChartLocalized = appstr("Base Charts");

  return (
    <>
      <div className={"ocn-file-manager-container"}>
        <Flex direction={"column"} height={"100%"} width={"25%"}>
          <div className={"ocn-file-manager-folder-container"}>
            <Flex padding={"12px 16px"}>
              <Text modifier="bold">{`${baseChartLocalized
                .charAt(0)
                .toUpperCase()}${baseChartLocalized.slice(1).toLowerCase()}`}</Text>
            </Flex>
            <div className={cx("ocn-file-manager-folder-table", "ocn-list-scrollbar")}>
              <List
                enableVirtualization={false}
                showCheck={false}
                value={selectedBaseChartID}
                options={baseChartOptions}
                listWidth="fill"
              />
            </div>
          </div>
        </Flex>
        <Flex direction={"column"} height={"100%"} width={"75%"}>
          <Flex
            padding={"12px 16px"}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={"16px"}
          >
            <PointInTimeStatus chart={selectedBaseChart} />
            <Flex direction={"row"} flex={"initial"} gap={"8px"}>
              {hasExcelExport && (
                <TooltipWrapper
                  tooltipProps={{
                    message: !isFullAccess ? appstr("exportfullaccessonly") : undefined
                  }}
                  fitReferenceToContent
                >
                  <Button
                    disabled={!isFullAccess || isRFCActive || isNullish(props.selectedPointInTime)}
                    onClick={handleExportArchive}
                    iconProps={{ name: "file-export" }}
                    className={cx("oc-special-secondary", "oc-special-secondary--medium")}
                    modifier={"secondary"}
                    dataTestId={"pit-exp-export-btn"}
                  >
                    {appstr("Export to Excel")}
                  </Button>
                </TooltipWrapper>
              )}
              <Button
                disabled={isRFCActive || isNullish(selectedBaseChart?.["Timestamp"])}
                onClick={handleManualArchive}
                iconProps={{ name: "box-archive" }}
                className={cx("oc-special-secondary", "oc-special-secondary--medium")}
                modifier={"secondary"}
                dataTestId={"pit-exp-manual-archive-btn"}
              >
                {appstr("Manually archive")}
              </Button>
            </Flex>
          </Flex>
          <PointInTimeControls
            chart={selectedBaseChart}
            dateMgr={{ dateFrom, dateTo, setDateFrom, setDateTo }}
          />
          <div className={cx("ocn-file-manager-file-table", "ocn-list-scrollbar")}>
            <Table
              dataTestId={"pit-nav-archive-table"}
              tableLayout={"fixed"}
              gridOptions={gridOptions}
              columns={BuildPITTableColumns()}
              data={tableData}
              showHeader={false}
              containerPadding={true}
              sortEnabled={true}
              rowSize="largeR"
              textSize="mediumT"
            />
          </div>
        </Flex>
      </div>
      {!isNullish(builderInfo) && (
        <PiTBuilderModal
          pitBuilderInfo={builderInfo}
          isCreating={isCreatingBuilder}
          show={true}
          onClose={closeBuilder}
          onCreate={handleCreate}
        />
      )}
      {configureBaseChart !== null ? (
        <PointInTimeModal
          show={configureBaseChart !== null}
          onClose={handleCloseConfigure}
          onUpdate={updateBaseChart}
          chartConfig={configureBaseChart}
          testIdPrefix={"pit-nav"}
        />
      ) : null}
    </>
  );
};
